import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

export type Mode = "light" | "dark" | "system";
export type ResolvedMode = "light" | "dark";

interface ThemeContextValue {
  mode: Mode;
  resolvedMode: ResolvedMode;
  setMode: (next: Mode) => void;
}

const STORAGE_KEY = "pentarab-admin-theme";
const ThemeContext = createContext<ThemeContextValue | null>(null);

function readStoredMode(): Mode {
  if (typeof window === "undefined") return "system";
  const v = window.localStorage.getItem(STORAGE_KEY);
  return v === "light" || v === "dark" || v === "system" ? v : "system";
}

function resolve(mode: Mode): ResolvedMode {
  if (mode !== "system") return mode;
  if (typeof window === "undefined") return "light";
  return window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
}

function applyToDocument(resolved: ResolvedMode) {
  if (typeof document === "undefined") return;
  const html = document.documentElement;
  html.setAttribute("data-theme", "default");
  html.setAttribute("data-mode", resolved);
  if (resolved === "dark") html.classList.add("dark");
  else html.classList.remove("dark");
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [mode, setModeState] = useState<Mode>(() => readStoredMode());
  const [resolvedMode, setResolvedMode] = useState<ResolvedMode>(() =>
    resolve(readStoredMode()),
  );

  const setMode = useCallback((next: Mode) => {
    setModeState(next);
    if (typeof window !== "undefined") {
      window.localStorage.setItem(STORAGE_KEY, next);
    }
    const resolved = resolve(next);
    setResolvedMode(resolved);
    applyToDocument(resolved);
  }, []);

  // Track OS scheme changes when mode === 'system'.
  useEffect(() => {
    if (mode !== "system" || typeof window === "undefined") return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = () => {
      const next: ResolvedMode = mq.matches ? "dark" : "light";
      setResolvedMode(next);
      applyToDocument(next);
    };
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, [mode]);

  // Cross-tab sync: when another tab writes the theme, mirror it here.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const onStorage = (e: StorageEvent) => {
      if (e.key !== STORAGE_KEY) return;
      const next =
        e.newValue === "light" || e.newValue === "dark" || e.newValue === "system"
          ? e.newValue
          : "system";
      setModeState(next);
      const resolved = resolve(next);
      setResolvedMode(resolved);
      applyToDocument(resolved);
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const value = useMemo(
    () => ({ mode, resolvedMode, setMode }),
    [mode, resolvedMode, setMode],
  );

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used inside <ThemeProvider>");
  return ctx;
}
